import React, { Fragment, MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface GIButtonProps {
    ariaLabel?: string;
    className?: string;
    dataTestId?: string;
    id: string;
    isDisabled?: boolean;
    icon?: React.ReactNode;
    iconPlacement?: 'start' | 'end',
    label?: string;
    labelId?: string;
    labelValue?: {[propName: string]: any;};
    name?: string;
    onClickHandler?: (e?: MouseEvent<HTMLButtonElement>) => void;
    render?: (item: any) => React.ReactNode;
    size?: '' | 'x-small' | 'small' | 'medium' | 'large';
    type?: 'submit' | 'button' | 'reset';
    variant?: 'primary'| 'secondary' | 'ghost' | 'link';
}

const GIButton = (props: GIButtonProps) => {
    const {
        id,
        labelId,
        labelValue,
        label,
        size = '',
        name,
        isDisabled = false,
        type = 'submit',
        variant='primary',
        icon,
        iconPlacement = 'end',
        className = '',
        ariaLabel = 'button',
        onClickHandler
    } = props;

    const buttonClasses = clsx({
        [`usa-button gi-button gi-button__${variant}`]: true,
        'gi-button--small': size === 'small',
        'gi-button--medium': size === 'medium',
        'gi-button--large': size === 'large',
        'gi-button__disabled': isDisabled,
        [className]: true
    });

    return (
        <button
            className={buttonClasses}
            type={type}
            name={name}
            disabled={isDisabled}
            data-automation-id={id}
            data-testid={id}
            onClick={onClickHandler}
            aria-label={ariaLabel}
        >
            {
                !icon &&
                <Fragment>
                    {
                        labelId && <FormattedMessage id={labelId} values={labelValue}/>
                    }
                    {
                        !labelId && label
                    }
                </Fragment>
            }
            {
                icon && iconPlacement === 'start' &&
                <Fragment>
                    {icon}
                    {
                        labelId && <FormattedMessage id={labelId} values={labelValue}/>
                    }
                    {
                        !labelId && label
                    }
                </Fragment>
            }
            {
                icon && iconPlacement === 'end' &&
                <Fragment>
                    {
                        labelId && <FormattedMessage id={labelId} values={labelValue}/>
                    }
                    {
                        !labelId && label
                    }
                    {icon}
                </Fragment>
            }
        </button>
    )
}

GIButton.propTypes = {
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
    dataTestId: PropTypes.string,
    iconName: PropTypes.object,
    iconClassName: PropTypes.string,
    iconPlacement: PropTypes.oneOf(['start', 'end']),
    id: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
    label: PropTypes.string,
    labelId: PropTypes.string,
    labelValue: PropTypes.object,
    name: PropTypes.string,
    onClickHandler: PropTypes.func,
    size: PropTypes.oneOf(['', 'x-small', 'small', 'medium', 'large']),
    type: PropTypes.oneOf(['submit', 'button', 'reset']),
    variant:PropTypes.oneOf(['primary' , 'secondary' , 'ghost' , 'link']),
}

GIButton.defaultProps = {
    ariaLabel: '',
    isDisabled: false,
    size: '',
    type: 'submit',
    variant: 'primary',
}

export default GIButton;
